import type { Filter } from '@kizen/filters/filter';
import type { FilterSet } from '@kizen/filters/filter-sets';
import type { FilterVariableEntry } from '@kizen/filters/variables';

import { useState } from 'react';
import { useFilterSets } from '@kizen/filters/hooks/use-filter-sets';
import { useSelector } from 'react-redux';
import { selectFilterMetadataDefinition } from 'store/filterMetaData/selectors';
import { PropsWithChildren, createContext, useContext } from 'react';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { validate as validateEmail } from 'components/Inputs/TextInput/presets/EmailAddress';

type Props = {
  config: Filter | FilterSet | FilterSet[];
  variables: FilterVariableEntry[];
  dirtyState: boolean;
  setDirtyState: (dirtyState: boolean) => void;
};

const ctx = createContext({});

export const useFilterDropdownContext = () => useContext(ctx);

/**
 * This is needed to share the useFilterSets instance between the two definitions of
 * ClientFilterDropdown (the table and chart views each define a new component). The state
 * management for ClientFilterDropdown used to be in redux. The new logic was put in a hook
 * and we use the key prop of ClientFilterDropdown to reload the data when changing groups.
 * This causes issues when switching between the chart & table view since there is a different
 * component structure for those pages. We define the useFilterSets hook here and pass the same
 * key prop to this component.
 *
 * @param {*} props.config - the 'loaded' filter config object with Filter class instances.
 */
export const FilterDropdownContext = ({
  config,
  variables,
  dirtyState,
  setDirtyState,
  children,
}: PropsWithChildren<Props>) => {
  const metaData = useSelector(selectFilterMetadataDefinition);
  const [generatingFilter, setGeneratingFilter] = useState<boolean>(false);
  const [filterSets, meta] = useFilterSets(
    metaData,
    variables,
    isValidPhoneNumber,
    validateEmail.withDomain,
    config
  );
  return (
    <ctx.Provider
      value={{
        filterSets,
        meta,
        generatingFilter,
        setGeneratingFilter,
        dirtyState,
        setDirtyState,
      }}
    >
      {children}
    </ctx.Provider>
  );
};
